<template>
  <Dialog
    v-model:visible="visible"
    header="Destroy Property"
    :modal="true"
    :style="{ width: '650px' }"
    @hide="resetForm"
  >
    <div class="destroy-form">
      <div class="warning-box">
        <i class="ri-alert-line"></i>
        <div>
          <strong>Warning: This action is permanent!</strong>
          <p>You are about to record the destruction of this property. This action cannot be undone.</p>
        </div>
      </div>

      <div class="info-box mb-2">
        <i class="ri-information-line"></i>
        Property: <strong>{{ property?.property_description || '-' }}</strong>
      </div>

      <div class="info-box">
        <i class="ri-information-line"></i>
        {{ isEditMode ? 'Destroyed Property:' : 'Destroy Property:' }} <strong>{{ formatForDisplay(displayQuantity) }}</strong>
      </div>

      <div class="form-group">
        <label>Date of Destruction: *</label>
        <Calendar
          v-model="form.destruction_date"
          dateFormat="yy-mm-dd"
          class="w-full"
          :maxDate="new Date()"
        />
      </div>

      <div class="form-section">
        <h5>Ordering Authority</h5>
        <div class="form-group">
          <label>Name: *</label>
          <InputText
            v-model="form.ordering_authority"
            placeholder="Enter authority name"
            class="w-full"
          />
        </div>
        <div class="form-group">
          <label>Designation:</label>
          <InputText
            v-model="form.ordering_authority_designation"
            placeholder="e.g., ACP, DCP, Commissioner"
            class="w-full"
          />
        </div>
      </div>

      <div class="form-group">
        <label>Remarks:</label>
        <Textarea
          v-model="form.remarks"
          rows="3"
          placeholder="Enter any additional remarks (court order reference, etc.)"
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="ri-close-line"
        @click="visible = false"
        class="p-button-text"
      />
      <Button
        :label="isEditMode ? 'Update Destroyed' : 'Destroy Property'"
        icon="ri-delete-bin-line"
        @click="submitDestroy"
        :loading="loading"
        class="p-button-danger"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

export default {
  name: 'DestroyPropertyDialog',
  components: {
    Dialog,
    Button,
    Calendar,
    InputText,
    Textarea
  },
  props: {
    property: {
      type: Object,
      required: true
    },
    availableQuantity: {
      type: Number,
      required: true
    },
    destructionDetails: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isWeightBased() {
      const name = (this.property?.property_type?.property_type || this.property?.PropertyType?.property_type || '').toString().toLowerCase();
      const keywords = ['drug', 'gold', 'silver', 'diamond', 'precious metal', 'narcotic', 'grams', 'contraband'];
      return keywords.some(kw => name.includes(kw));
    },
    latestDestructionRecord() {
      if (!this.destructionDetails?.length) return null;
      const copy = [...this.destructionDetails];
      copy.sort((a, b) => {
        const ad = new Date(a.destruction_date || 0).getTime();
        const bd = new Date(b.destruction_date || 0).getTime();
        return bd - ad;
      });
      return copy[0] || null;
    },
    isEditMode() {
      return !!this.latestDestructionRecord;
    },
    displayQuantity() {
      if (this.isEditMode) {
        return Number(this.latestDestructionRecord?.quantity_destroyed || 0);
      }
      return this.availableQuantity;
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      form: {
        destruction_date: new Date(),
        ordering_authority: '',
        ordering_authority_designation: '',
        remarks: ''
      },
      currentDestructionId: null
    };
  },
  methods: {
    open() {
      this.visible = true;
      this.resetForm();
      this.prefillFromExistingRecord();
    },
    resetForm() {
      this.form = {
        destruction_date: new Date(),
        ordering_authority: '',
        ordering_authority_designation: '',
        remarks: ''
      };
      this.currentDestructionId = null;
    },
    prefillFromExistingRecord() {
      const latest = this.latestDestructionRecord;
      if (!latest) return;
      this.currentDestructionId = latest.destruction_id || null;
      this.form = {
        destruction_date: this.toDateOrNow(latest.destruction_date),
        ordering_authority: latest.ordering_authority || '',
        ordering_authority_designation: latest.ordering_authority_designation || '',
        remarks: latest.remarks || ''
      };
    },
    toDateOrNow(value) {
      if (!value) return new Date();
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return new Date();
      return dt;
    },
    formatQuantity(qty) {
      const n = Number(qty);
      if (qty == null || qty === '' || isNaN(n)) return '0 g';
      if (n >= 1000) {
        return `${(n / 1000).toFixed(3)} kg`;
      }
      return `${n.toFixed(3)} g`;
    },
    formatCount(qty) {
      const n = Number(qty);
      if (qty == null || qty === '' || isNaN(n)) return '0 nos.';
      return `${Math.floor(n)} nos.`;
    },
    formatForDisplay(qty) {
      return this.isWeightBased ? this.formatQuantity(qty) : this.formatCount(qty);
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async submitDestroy() {
      if (!this.isEditMode && (!this.availableQuantity || this.availableQuantity <= 0)) {
        this.$store.dispatch('error', 'No quantity available at PS');
        return;
      }

      if (!this.form.destruction_date) {
        this.$store.dispatch('error', 'Please select a date');
        return;
      }

      if (!this.form.ordering_authority.trim()) {
        this.$store.dispatch('error', 'Please enter ordering authority name');
        return;
      }

      this.loading = true;
      try {
        if (this.isEditMode) {
          if (!this.currentDestructionId) {
            this.$store.dispatch('error', 'Unable to identify existing destruction record');
            return;
          }
          await this.$store.dispatch('post', {
            uri: 'updateDestructionDetail',
            data: {
              property_id: this.property.property_id,
              destruction_id: this.currentDestructionId,
              destruction_date: this.formatDate(this.form.destruction_date),
              ordering_authority: this.form.ordering_authority,
              ordering_authority_designation: this.form.ordering_authority_designation,
              remarks: this.form.remarks
            }
          });
          this.$store.dispatch('success', 'Destroyed details updated successfully');
        } else {
          await this.$store.dispatch('post', {
            uri: 'destroyProperty',
            data: {
              property_id: this.property.property_id,
              quantity_destroyed: this.availableQuantity,
              destruction_date: this.formatDate(this.form.destruction_date),
              ordering_authority: this.form.ordering_authority,
              ordering_authority_designation: this.form.ordering_authority_designation,
              destruction_method: null,
              witnesses: null,
              remarks: this.form.remarks,
              sync_available_at_ps: this.availableQuantity
            }
          });
          this.$store.dispatch('success', 'Property destroyed successfully');
        }
        this.$emit('refresh');
        this.visible = false;
      } catch (error) {
        const msg = error.response?.data?.message || '';
        if (msg && (msg.includes('Insufficient quantity') || msg.includes('insufficient'))) {
          this.$store.dispatch('error', 'Insufficient quantity available at PS. If you recently updated the quantity in the form, please save the form first (click Update/Submit), then try again.');
        } else {
          this.$store.dispatch('error', msg || 'Failed to destroy property');
        }
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.destroy-form {
  padding: 10px 0;
}

.warning-box {
  background: #FFEBEE;
  border: 2px solid #F44336;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  color: #C62828;
}

.warning-box i {
  font-size: 24px;
  flex-shrink: 0;
}

.warning-box strong {
  display: block;
  margin-bottom: 5px;
}

.warning-box p {
  margin: 0;
  font-size: 13px;
}

.info-box {
  background: #E3F2FD;
  border: 1px solid #2196F3;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1976D2;
}

.info-box i {
  font-size: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-section {
  background: #F9F9F9;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 15px;
}

.form-section h5 {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #555;
  font-weight: 600;
}

.w-full {
  width: 100%;
}
</style>
